
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Users, Clock, MapPin, Settings, Search, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Referral } from '@/types/listings';
import ListingSearch from '@/components/listings/ListingSearch';
import MyReferrals from '@/components/referrals/MyReferrals';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import Navigation from '@/components/Navigation';

const CaseManagerDashboard = () => {
  const { user, profile, signOut } = useAuth();
  const { toast } = useToast();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (user) {
      fetchReferrals();
    }
  }, [user]);

  const fetchReferrals = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referring_user', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReferrals((data || []) as Referral[]);
    } catch (error) {
      console.error('Error fetching referrals:', error);
      toast({
        title: 'Error',
        description: 'Failed to load referrals',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const activeCases = referrals.filter(r => ['pending', 'accepted'].includes(r.status)).length;
  const pendingReferrals = referrals.filter(r => r.status === 'pending').length;
  const acceptedReferrals = referrals.filter(r => r.status === 'accepted').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <Navigation />
      <header className="bg-white border-b border-border/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Case Manager Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {profile?.name || user?.email}</p>
          </div>
          <div className="flex items-center space-x-4">
            <Badge variant="outline">{profile?.role?.replace('_', ' ')}</Badge>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/profile'}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" size="sm" onClick={signOut}>
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Cases</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeCases}</div>
              <p className="text-xs text-muted-foreground">{pendingReferrals} pending responses</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Placed This Month</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{acceptedReferrals}</div>
              <p className="text-xs text-muted-foreground">Successful placements</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{referrals.length}</div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Search Available Beds</CardTitle>
                <Dialog open={showSearch} onOpenChange={setShowSearch}>
                  <DialogTrigger asChild>
                    <Button>
                      <Search className="w-4 h-4 mr-2" />
                      Search Beds
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Find Available Beds</DialogTitle>
                    </DialogHeader>
                    <ListingSearch />
                  </DialogContent>
                </Dialog>
              </div>
              <CardDescription>
                Find suitable placements for your clients
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-center py-8">
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium mb-2">Search for Available Beds</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Use advanced filters to find the perfect placement for your clients
                  </p>
                  <Button onClick={() => setShowSearch(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Start Search
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>My Referrals</CardTitle>
              <CardDescription>
                Track your referral submissions and outcomes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MyReferrals />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CaseManagerDashboard;
